import { Switch as CompassSwitch } from "@circleco/compass/components/Switch";
import { omit } from "lodash";
import type { ComponentPropsWithRef, ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";

import type { ToggleSwitchProps as UIKitFormToggleSwitchProps } from "@/react/components/shared/uikit/Form/ToggleSwitch";
import { ToggleSwitch as UIKitFormToggleSwitch } from "@/react/components/shared/uikit/Form/ToggleSwitch";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

// eslint-disable-next-line no-barrel-files/no-barrel-files -- This is a compatibility layer that re-exports types for SettingsApp
export type { ToggleSwitchProps } from "@/react/components/shared/uikit/Form/ToggleSwitch";

type CompassSwitchProps = ComponentPropsWithRef<typeof CompassSwitch>;

interface CompassFormToggleSwitchProps extends UIKitFormToggleSwitchProps {
  description?: ReactNode;
  error?: string;
  inlineReverse?: boolean;
  invalid?: boolean;
  labelPosition?: "start" | "end";
  labelTooltip?: string;
  layout?: "horizontal" | "vertical";
  placeholder?: string;
  required?: boolean;
  rootProps?: Record<string, unknown>;
  transformValue?: (value: unknown) => unknown;
}

export const FormToggleSwitch = (props: CompassFormToggleSwitchProps) => {
  const isCircleV4 = useIsCircleV4FeatureEnabled(
    "circle_v4_form_elements_enabled",
  );
  const { control } = useFormContext();

  if (isCircleV4) {
    const {
      name,
      id,
      rules,
      description,
      error,
      invalid: isInvalid = false,
      labelPosition = props?.inlineReverse ? "end" : "start",
      labelTooltip,
      onToggleChange,
      disabled: isDisabled = false,
      label,
      className,
      wrapperClass,
      dataTestId,
      ariaLabel,
      required: isRequired = false,
      rootProps,
      layout,
    } = props;

    const compassLayout = layout === "horizontal" ? "horizontal" : undefined;

    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => {
          const rootClassName = classNames(
            {
              // Legacy UIKit toggles without label/description were compact-width.
              // Compass Switch defaults to full width, which can break ad-hoc row layouts.
              "w-fit max-w-fit": !label && !description,
            },
            wrapperClass,
          );

          const compassProps: CompassSwitchProps = {
            rootProps,
            name,
            checked: Boolean(field.value),
            onCheckedChange: (checked: boolean) => {
              field.onChange(checked);
              onToggleChange?.(checked);
            },
            onBlur: field.onBlur,
            disabled: isDisabled,
            label,
            className,
            description,
            labelPosition,
            labelTooltip,
            required: isRequired,
            id,
            rootClassName: rootClassName || undefined,
            ...(ariaLabel && { ariaLabel }),
            ...(dataTestId && { "data-testid": dataTestId }),
            ...(isInvalid && { "aria-invalid": true }),
            ...(error && { "aria-errormessage": error }),
            ...(compassLayout != null ? { layout: compassLayout } : {}),
          };

          return <CompassSwitch {...compassProps} ref={field.ref} />;
        }}
      />
    );
  }

  return <UIKitFormToggleSwitch {...omit(props, ["layout", "rootProps"])} />;
};

FormToggleSwitch.displayName = "Form.ToggleSwitch";
